import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  FormControlLabel, 
  Checkbox, 
  Grid,
  FormHelperText
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Create styled components with ultra-tiny text
const TermsText = styled('div')(({ theme }) => ({
  fontSize: '0.5px',  // Extremely small font size
  lineHeight: 0.5,    // Very tight line height
  transform: 'scale(0.1)',  // Scale down even further
  transformOrigin: 'top left',
  letterSpacing: '-0.1em',  // Tighter letter spacing
  wordSpacing: '-0.1em',    // Tighter word spacing
  maxWidth: '1000%',  // Increased to compensate for the smaller scale
  margin: 0,
  padding: 0,
  '& strong': {
    fontSize: '0.5px',
    fontWeight: 'bold'
  },
  '& p': {
    fontSize: '0.5px',
    margin: '0'
  }
}));

/**
 * Terms component
 * Displays terms and conditions with checkboxes for agreement
 * Checkboxes are only enabled after the user has scrolled to the bottom
 */
const Terms = forwardRef(({
  title = 'RENTAL AGREEMENT AND LIABILITY WAIVER',
  content,
  termsHtml, // renamed from termsUrl
  hasAgreed,
  onAgreementChange,
  hasAgreedToElectronic,
  onElectronicAgreementChange,
  disabled = false,
  instructionText = null,
  onValidationChange = null,
  showValidationErrors = false
}, ref) => {
  const [validationError, setValidationError] = useState('');
  const [hasReadTerms, setHasReadTerms] = useState(false);
  const textBoxRef = useRef(null);
  
  // Handle scroll event for the text box
  const handleTextBoxScroll = useCallback(() => {
    if (textBoxRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = textBoxRef.current;
      const scrollableHeight = scrollHeight - clientHeight;
      if (scrollableHeight <= 0) {
        setHasReadTerms(true);
        return;
      }
      // Only set hasReadTerms when scrolled to the bottom
      if (scrollTop + clientHeight >= scrollHeight - 2) {
        setHasReadTerms(true);
      } else {
        setHasReadTerms(false);
      }
    }
  }, []);

  // Enable the checkboxes once the terms are readable: IMMEDIATELY when the content doesn't
  // overflow the box (no scroll is even possible), otherwise after the user scrolls to the bottom
  // (handled by onScroll). Re-checks on resize so a late layout / short blurb still unlocks —
  // fixes the deadlock where short terms never fire a scroll event and the checkboxes stay disabled.
  useEffect(() => {
    const el = textBoxRef.current;
    setHasReadTerms(false);
    if (!el) return undefined;
    const check = () => {
      if (el.scrollHeight - el.clientHeight <= 0) setHasReadTerms(true);
    };
    check();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [termsHtml, content]);

  // Expose validation method via ref
  useImperativeHandle(ref, () => ({
    validate: () => validate(),
    isValid: () => {
      const error = validate(false);
      return error === null;
    }
  }));
  
  // Validation function
  const validate = useCallback((setErrors = true) => {
    if (!hasAgreed) {
      const error = 'You must agree to the terms and conditions';
      if (setErrors && showValidationErrors) {
        setValidationError(error);
      }
      return error;
    }
    
    if (!hasAgreedToElectronic) {
      const error = 'You must consent to use electronic records and signatures';
      if (setErrors && showValidationErrors) {
        setValidationError(error);
      }
      return error;
    }
    
    if (setErrors && showValidationErrors) {
      setValidationError('');
    }
    return null;
  }, [hasAgreed, hasAgreedToElectronic, showValidationErrors]);
  
  // Update validation state when checkbox states change or showValidationErrors changes
  useEffect(() => {
    if (onValidationChange) {
      const isValid = validate(false) === null;
      onValidationChange(isValid);
    }
    
    // Only update the error message if we're showing validation errors
    if (showValidationErrors) {
      validate();
    }
  }, [hasAgreed, hasAgreedToElectronic, showValidationErrors, onValidationChange, validate]);
  

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        {title}
      </Typography>
      <Box
        ref={textBoxRef}
        onScroll={handleTextBoxScroll}
        sx={{
          maxHeight: 300,
          overflowY: 'auto',
          border: '1px solid #ccc',
          borderRadius: 2,
          p: 2,
          mb: 2,
          background: '#fafafa',
        }}
        tabIndex={0}
        aria-label="Terms and Conditions"
      >
        {termsHtml ? (
          <div dangerouslySetInnerHTML={{ __html: termsHtml }} />
        ) : (
          <TermsText>{content}</TermsText>
        )}
      </Box>
      
      {/* Only show the checkboxes when the user has scrolled to the bottom */}
      <FormControlLabel
        control={
          <Checkbox
            checked={hasAgreed}
            onChange={(e) => onAgreementChange(e.target.checked)}
            disabled={disabled || !hasReadTerms}
            color="primary"
          />
        }
        label="I have read and agree to the terms and conditions"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={hasAgreedToElectronic}
            onChange={(e) => onElectronicAgreementChange(e.target.checked)}
            disabled={disabled || !hasReadTerms}
            color="primary"
          />
        }
        label="I consent to use electronic records and signatures for this transaction"
        sx={{ mt: 1 }}
      />
      
      {/* Validation error message */}
      {validationError && showValidationErrors && (
        <Grid item xs={12}>
          <FormHelperText error>{validationError}</FormHelperText>
        </Grid>
      )}
      
      {instructionText && (
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            {instructionText}
          </Typography>
        </Grid>
      )}
    </Box>
  );
});

export default Terms;
